import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, Col, Form, Row, InputNumber } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import CompaniesApi from 'libs/api/admin/companies';
import { useRecoilValue } from 'recoil';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import companiesAdapter from './companies.adapter';
import { useApi } from 'hooks/useApi';
import ErrorHelper from 'helpers/ErrorHelper';
import { getFilters } from 'hooks/useShops';
import { getPage, getUser } from '../../../../hooks/useUser';
import FinanceInvoices from 'libs/api/finance-invoices';
import LinkWithParams from 'components/linkWithParams';
import { useNavigate } from 'hooks/useNavigate';

const defaultState = {
  companies: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const getState = () => ({
  companies: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
});

const api = new CompaniesApi();
const financeInvoicesApi = new FinanceInvoices();

export const useCompanies = (isMainPage = false) => {
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useRecoilValue(getUser);
  const pages = useRecoilValue(getPage);
  const isAdmin = user?.superadmin || user?.master_admin;
  const [currentShppingCenter, setCurrentShppingCenter] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  const [returnType, setReturnType] = useState('');
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);
  const [updateInProcess, setUpdateInProcess] = useState(false);
  const onChangeTable = useTableChangeFilterHook();
  const { handleChangeFilters, handleCloseFilter, searchParams } = useFilter();
  const filtersData = useRecoilValue(getFilters);
  const {
    singleItem,
    onUpdate: apiOnUpdate,
    loading: apiLoading,
    setSingleItem,
  } = useApi({
    isMainPage,
    resourceKey: 'companies',
    singleResourceKey: 'company',
    api,
  });

  const [data, setData] = useState(getState());

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { companies, pagination = {} },
      } = await api.list({ params });
      setData((s) => ({
        ...s,
        companies: [...companiesAdapter(companies || [])],
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isMainPage) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data.pagination.current, data.sorting.order, isMainPage]);

  const { t } = useTranslation();

  const handleGenerateInvoice = useCallback(
    async (id) => {
      try {
        await financeInvoicesApi.generateInvoice(id);
        // Helpers.downloadFile(response, 'invoice.xlsx');
      } catch (e) {
        const error = e?.response?.data?.errors || [t('Something went wrong!')];
        UIHelper.openNotificationWithIcon('error', error.join('\n'));
      }
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 100,
      },
      {
        title: t('Nego ID'),
        dataIndex: 'nego_id',
        width: 150,
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Catena'),
        dataIndex: 'chains.title',
        sorter: () => {},
        render: (data, row) => row?.chain,
      },
      {
        title: t('Outlet Village'),
        dataIndex: 'mall_title',
        sorter: () => {},
      },

      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: isAdmin ? 230 : 190,
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false ||
            isAdmin) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
              {isAdmin && (
                <Col>
                  <Button
                    icon={<FileTextOutlined />}
                    type={'green'}
                    onClick={() => {
                      handleGenerateInvoice(row.id);
                    }}
                  />
                </Col>
              )}
            </Row>
          ),
      },
      {
        title: '',
        dataIndex: 'published',
        width: 150,
        sorter: () => {},
        render: (data, row) => (
          <>
            {user?.master_admin && (
              <LinkWithParams to={`${pathname}/${row.id}/status`}>
                <Badge
                  status={data ? 'success' : 'error'}
                  text={data ? t('Attiva') : t('Disattiva')}
                />
              </LinkWithParams>
            )}
            {!user?.master_admin && (
              <Badge
                status={data ? 'success' : 'error'}
                text={data ? t('Attiva') : t('Disattiva')}
              />
            )}
          </>
        ),
      },
    ],
    [t, pathname, user?.master_admin, pages?.ability?.can_read, isAdmin],
  );

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        isActiveSearch: true,
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filtersData?.shopping_centers?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Status'),
        type: 'select',
        name: 'by_published',
        values: [
          {
            value: 'false',
            title: t('Insegna disattiva'),
          },
          {
            value: 'true',
            title: t('Insegna attiva'),
          },
        ],
      },
      {
        title: t('Categoria'),
        type: 'select',
        name: 'by_category',
        values: filtersData?.company_categories?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Catena'),
        type: 'select',
        name: 'by_chain',
        values: filtersData?.chains?.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
    ],
    [t, filtersData],
  );

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  const onUpdate = async (data, id) => {
    setUpdateInProcess(true);
    try {
      await api.updateCompany(`${id}`, data);
      navigate(UIHelper.getListRoute(pathname));
      fetchData();
    } catch (e) {
      const errors = e.response.data || [];
      if (errors['company_employee_contacts.email']) {
        UIHelper.openNotificationWithIcon(
          'error',
          errors['company_employee_contacts.email'][0],
        );
      }
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          'company_click_collect.api_key':
            'company_click_collect_attributes.api_key',
          'company_click_collect.shop_login':
            'company_click_collect_attributes.shop_login',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const onUpdateStatus = async (id) => {
    const currentCompany = data.companies.find((i) => i.id === +id);
    setUpdateInProcess(true);
    try {
      await api.onUpdateStatus(
        `${id}`,
        currentCompany?.published ? 'disactive' : 'active',
      );
      // disactive
      navigate(UIHelper.getListRoute(pathname));
      await fetchData();
    } catch (e) {
      const errors = e.response.data || [];
      if (errors['company_employee_contacts.email']) {
        UIHelper.openNotificationWithIcon(
          'error',
          errors['company_employee_contacts.email'][0],
        );
      }

      if (errors['nego_id']) {
        UIHelper.openNotificationWithIcon('error', errors['nego_id'][0]);

        navigate(UIHelper.getListRoute(pathname));
      }

      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          'company_click_collect.api_key':
            'company_click_collect_attributes.api_key',
          'company_click_collect.shop_login':
            'company_click_collect_attributes.shop_login',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const onDelete = async (id) => {
    setLoading(true);
    try {
      await api.destroy(id);
      navigate(UIHelper.getListRoute(pathname));
      fetchData();
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };
  const removeDiacritics = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const currentActiveItem = document.querySelector(
        '.ant-menu-item-selected',
      ).textContent;
      let fileTitle = '';
      if (currentActiveItem.trim().split(/\s+/).length > 1) {
        fileTitle += `${removeDiacritics(currentActiveItem)
          .replace(/\s+/g, '_')
          .toLowerCase()}`;
      } else {
        fileTitle += `${removeDiacritics(currentActiveItem).toLowerCase()}`;
      }
      const currentDate = new Date();
      const formattedDate = `${currentDate.getDate()}_${(
        currentDate.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}_${currentDate
        .getFullYear()
        .toString()
        .padStart(2, '0')}`;
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, `${fileTitle}_${formattedDate}.xlsx`);
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async (data) => {
    try {
      setUpdateInProcess(true);
      await api.createCompany(data);
      navigate(UIHelper.getListRoute(pathname, 1));
      fetchData();
    } catch (e) {
      const arr = [];
      const {
        response: { data },
      } = e;
      // setFormErrors(arr);
      form.setFields(
        ErrorHelper.getErrorsForForm(data || [], {
          company_category: 'company_category_id',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  const onAction = (data) => {
    const formData = new FormData();
    formData.append('file', data);
    for (const dataKey in data) {
      switch (dataKey) {
        case 'shop_policy_click_collect':
        case 'company_image_click_collect':
        case 'logo_click_collect':
        case 'header_image_click_collect': {
          if (data[dataKey] && data[dataKey][0]) {
            formData.append(
              `company[company_click_collect_attributes][${dataKey}]`,
              data[dataKey][0].originFileObj,
            );
          }
          break;
        }
        case 'company_employee_contacts_attributes': {
          if (data[dataKey]) {
            for (let index in data[dataKey]) {
              // existing contacts send cleared fields too, so the backend can empty them
              const isExisting = !!data[dataKey][index].id;
              for (const key in data[dataKey][index]) {
                const value = data[dataKey][index][key];
                if (value || isExisting) {
                  formData.append(
                    `company[${dataKey}][${index}][${key}]`,
                    value ?? '',
                  );
                }
              }
            }
          }
          break;
        }
        case 'company_click_collect_attributes': {
          if (data[dataKey]) {
            for (const dataKey2 in data[dataKey]) {
              if (
                data[dataKey][dataKey2] ||
                data[dataKey][dataKey2] === false
              ) {
                formData.append(
                  `company[company_click_collect_attributes][${dataKey2}]`,
                  data[dataKey][dataKey2],
                );
              }
            }
          }
          break;
        }
        case 'company_data_attributes': {
          if (data[dataKey]) {
            for (const dataKey2 in data[dataKey]) {
              formData.append(
                `company[company_data_attributes][${dataKey2}]`,
                data[dataKey][dataKey2],
              );
            }
          }
          break;
        }
        case 'company_table_prices': {
          const keys = [
            'to_30',
            'from_30_to_50',
            'from_50_to_100',
            'from_100_to_150',
            'from_150_to_200',
            'from_200_to_250',
            'from_250',
          ];
          if (data[dataKey]['0']) {
            keys.forEach((key) => {
              formData.append(
                `company[company_table_prices_attributes][0][${key}]`,
                data[dataKey]['0'][key],
              );
            });
            formData.append(
              'company[company_table_prices_attributes][0][price_type]',
              'delivery',
            );
            if (singleItem) {
              const [comp1, comp2] =
                singleItem?.company_click_collect?.company_table_prices || [];
              if (comp1) {
                formData.append(
                  'company[company_table_prices_attributes][0][id]',
                  comp1.id,
                );
              }
            }
          }
          if (data[dataKey]['1']) {
            keys.forEach((key) => {
              formData.append(
                `company[company_table_prices_attributes][1][${key}]`,
                data[dataKey]['1'][key],
              );
            });
            formData.append(
              'company[company_table_prices_attributes][1][price_type]',
              'return',
            );
            if (singleItem) {
              const [comp1, comp2] =
                singleItem?.company_click_collect?.company_table_prices || [];
              if (comp1) {
                formData.append(
                  'company[company_table_prices_attributes][1][id]',
                  comp2.id,
                );
              }
            }
          }
          break;
        }
        case 'company_competitors': {
          if (data[dataKey]['0']) {
            formData.append(
              'company[company_competitors_attributes][0][company_id]',
              id,
            );
            formData.append(
              'company[company_competitors_attributes][0][competitor_id]',
              data[dataKey]['0'],
            );
          }
          if (data[dataKey]['1']) {
            formData.append(
              'company[company_competitors_attributes][1][company_id]',
              id,
            );
            formData.append(
              'company[company_competitors_attributes][1][competitor_id]',
              data[dataKey]['1'],
            );
          }
          if (singleItem) {
            const [comp1, comp2] = singleItem?.company_competitors || [];
            if (comp1) {
              formData.append(
                'company[company_competitors_attributes][0][id]',
                comp1.id,
              );
            }
            if (comp1) {
              formData.append(
                'company[company_competitors_attributes][1][id]',
                comp2.id,
              );
            }
          }
          break;
        }
        default:
          formData.append(`company[${dataKey}]`, data[dataKey]);
          break;
      }
    }
    if (id) {
      return onUpdate(formData, id);
    }

    return onCreate(formData);
  };

  useEffect(() => {
    (async () => {
      if (currentShppingCenter) {
        try {
          const {
            data: { companies = [] },
          } = await api.list({
            params: {
              per: 10000,
              by_published: true,
              sortable_column: 'title',
              direction: 'ASC',
              by_shopping_center: currentShppingCenter,
            },
          });
          setContractors(companies);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [currentShppingCenter]);

  useEffect(() => {
    if (singleItem) {
      setCurrentShppingCenter(singleItem?.shopping_center?.id);
    }
  }, [singleItem]);

  const handleDeleteFile = async (data) => {
    setUpdateInProcess(true);
    try {
      const {
        file: { id },
        param,
      } = data;
      await api.deleteFile(singleItem.id, id);
      setSingleItem((s) => ({
        ...s,
        company_click_collect: {
          ...s.company_click_collect,
          [param]: undefined,
        },
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    loading,
    handleChangeFilters,
    handleCloseFilter,
    searchParams,
    onChangeTableFilter,
    handleDownloadExcel,
    onUpdate,
    updateInProcess,
    onDelete,
    form,
    companyCategories: filtersData?.company_categories,
    companyChildCategories: filtersData?.company_child_categories,
    prestigeLevels: filtersData?.prestige_levels,
    singleItem,
    apiOnUpdate,
    onCreate,
    onAction,
    chains: filtersData?.chains,
    formErrors,
    setFormErrors,
    apiLoading,
    filtersData,
    setCurrentShppingCenter,
    contractors,
    handleDeleteFile,
    setDeliveryType,
    deliveryType,
    returnType,
    setReturnType,
    onUpdateStatus,
    user,
  };
};
